import React, { useState, useEffect, useCallback } from 'react';
import { oltLogsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { ScrollText, RefreshCw, Power, Wifi, WifiOff, Thermometer, Cable, ArrowDownUp, Settings2, Tag, Loader2, Search, AlertTriangle } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

// Admin-only OLT activity log — merged, durable timeline of every status transition the
// alarm poller has ever recorded (ONU online/offline, PON port down, uplink down, high
// temperature, OLT unreachable, OLT rebooted) plus every admin command sent to the OLT
// (reboot, delete, provision, firmware upgrade, activate/deactivate, label/profile changes).
// Both sources live in MongoDB, so this history survives the physical OLT rebooting or
// losing its own state — the OLT itself keeps no real log of its own (see
// backend oltService.getAlarms()).

const KIND_META = {
  olt_rebooted:     { label: 'OLT Rebooted',   icon: Power,      color: 'var(--c-b45309)', bg: 'var(--cb-fef3c7)' },
  olt_offline:      { label: 'OLT Unreachable',icon: WifiOff,    color: 'var(--c-991b1b)', bg: 'var(--cb-fee2e2)' },
  onu_offline:      { label: 'ONU Status',      icon: Wifi,       color: 'var(--c-374151)', bg: 'var(--cb-f3f4f6)' },
  pon_port_down:    { label: 'PON Port Down',   icon: Cable,      color: 'var(--c-991b1b)', bg: 'var(--cb-fee2e2)' },
  uplink_down:      { label: 'Uplink Down',     icon: ArrowDownUp,color: 'var(--c-991b1b)', bg: 'var(--cb-fee2e2)' },
  high_temperature: { label: 'High Temperature',icon: Thermometer,color: 'var(--c-b45309)', bg: 'var(--cb-fef3c7)' },
  admin_action:     { label: 'Admin Action',    icon: Settings2,  color: 'var(--c-1e3a8a)', bg: 'var(--cb-dbeafe)' },
  onu_label:        { label: 'Label/Profile',   icon: Tag,        color: 'var(--c-0e9e96)', bg: 'var(--cb-d1fae5)' },
  mac_lookup:       { label: 'MAC Lookup',      icon: Search,     color: 'var(--c-6d28d9)', bg: 'var(--cb-ede9fe)' },
  mac_lookup_error: { label: 'MAC-lookup Error', icon: AlertTriangle, color: 'var(--c-991b1b)', bg: 'var(--cb-fee2e2)' },
};

const TYPE_FILTERS = [
  { key: '', label: 'All' },
  { key: 'olt_rebooted', label: 'OLT Reboots' },
  { key: 'admin_action', label: 'Admin Actions' },
  { key: 'onu_offline', label: 'ONU Status' },
  { key: 'pon_port_down', label: 'PON Port Down' },
  { key: 'uplink_down', label: 'Uplink Down' },
  { key: 'high_temperature', label: 'High Temp' },
  { key: 'onu_label', label: 'Label/Profile' },
  { key: 'olt_offline', label: 'OLT Unreachable' },
  { key: 'mac_lookup', label: 'MAC Lookups' },
  { key: 'mac_lookup_errors', label: 'MAC-lookup Errors' },
];

function dayLabel(iso) {
  const d = new Date(iso), today = new Date(), yest = new Date();
  yest.setDate(today.getDate() - 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yest)) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
}

function LogRow({ row }) {
  const meta = KIND_META[row.kind] || { label: row.kind, icon: ScrollText, color: 'var(--c-374151)', bg: 'var(--cb-f3f4f6)' };
  const Icon = meta.icon;
  // MAC lookups the OLT never answered (or that errored) are the ones to look at after a
  // reboot — highlight them so they stand out in the timeline.
  const critical = row.source === 'maclookup' && row.severity === 'critical';
  const warning = row.source === 'maclookup' && row.severity === 'warning';
  return (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px',
      border: `1px solid ${critical ? 'var(--cb-fca5a5)' : warning ? 'var(--cb-fcd34d)' : 'var(--cb-e5e7eb)'}`, borderRadius: 10,
      background: critical ? 'var(--cb-fef2f2)' : warning ? 'var(--cb-fffbeb)' : 'var(--cb-ffffff)',
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 8, background: meta.bg, color: meta.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={17} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: '.72rem', fontWeight: 700, color: meta.color, textTransform: 'uppercase', letterSpacing: '.02em' }}>
            {meta.label}
          </span>
          <span style={{ fontSize: '.75rem', color: 'var(--text-muted)', flexShrink: 0 }}>
            {new Date(row.at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <div style={{ fontSize: '.88rem', fontWeight: 500, marginTop: 3, wordBreak: 'break-word' }}>{row.message}</div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
          {row.detail && <span style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{row.detail}</span>}
          {row.actor && <span style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>by {row.actor}</span>}
        </div>
      </div>
    </div>
  );
}

export default function OltLogsPage() {
  useDarkReady();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [type, setType] = useState('');

  const load = useCallback((p, t) => {
    (p === 1 ? setLoading : setLoadingMore)(true);
    oltLogsAPI.getFeed({ page: p, limit: 50, type: t || undefined })
      .then(({ data }) => {
        setRows(prev => (p === 1 ? data.data : [...prev, ...data.data]));
        setHasMore(!!data.hasMore);
      })
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load OLT logs'))
      .finally(() => { setLoading(false); setLoadingMore(false); });
  }, []);

  useEffect(() => { setPage(1); load(1, type); }, [type, load]);
  useEffect(() => { oltLogsAPI.getCounts().then(({ data }) => setCounts(data.data)).catch(() => {}); }, []);

  const loadMore = () => { const next = page + 1; setPage(next); load(next, type); };
  const refresh = () => { setPage(1); load(1, type); oltLogsAPI.getCounts().then(({ data }) => setCounts(data.data)).catch(() => {}); };

  let lastDay = null;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ScrollText size={22} /> OLT Logs
          </h1>
          <p className="page-subtitle">
            Every OLT status change, admin action and MAC lookup, permanently recorded in the CRM — survives OLT reboots and power loss. Admin only.
          </p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={refresh} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {counts && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {[
            { num: counts.reboots, lbl: 'OLT Reboots (all time)' },
            { num: counts.adminActions, lbl: 'Admin Actions Logged' },
            { num: counts.activeAlarms, lbl: 'Active Alarms' },
            { num: counts.macNoReply ?? 0, lbl: 'MAC Lookups With No Reply' },
            { num: counts.macErrors ?? 0, lbl: 'MAC-lookup Errors' },
          ].map(s => (
            <div key={s.lbl} className="stat-card">
              <div className="stat-number">{s.num}</div>
              <div className="stat-label">{s.lbl}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {TYPE_FILTERS.map(f => (
          <button
            key={f.key}
            type="button"
            onClick={() => setType(f.key)}
            style={{
              font: 'inherit', fontSize: '.82rem', minHeight: 36, padding: '0 14px', borderRadius: 20,
              cursor: 'pointer', border: `1.5px solid ${type === f.key ? 'var(--primary)' : 'var(--cb-e5e7eb)'}`,
              background: type === f.key ? 'var(--primary)' : 'var(--cb-ffffff)',
              color: type === f.key ? 'var(--c-ffffff)' : 'var(--c-374151)', fontWeight: 600,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          <Loader2 size={24} style={{ animation: 'spin .7s linear infinite' }} /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          No log entries for this filter yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {rows.map((row, i) => {
            const day = dayLabel(row.at);
            const showDay = day !== lastDay;
            lastDay = day;
            return (
              <React.Fragment key={`${row.source}-${row.at}-${i}`}>
                {showDay && (
                  <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--text-muted)', marginTop: i === 0 ? 0 : 6 }}>
                    {day}
                  </div>
                )}
                <LogRow row={row} />
              </React.Fragment>
            );
          })}
        </div>
      )}

      {!loading && hasMore && (
        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <button type="button" className="btn btn-secondary" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
